import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield,
  ShieldCheck,
  FileText,
  Eye,
  Loader2,
  Save,
  RotateCcw,
  Info,
  Lock,
  Plus,
  Check,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import Checkbox from 'components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import Modal from 'components/ui/Modal';
import { cn } from 'lib/utils';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as Permission from 'modules/permission';
import { toast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';

interface PermissionDefinition {
  key: string;
  category: string;
  resource?: string;
  resourceOrder?: number;
  action?: string;
  feature: string;
  description: string;
}
type PermissionMatrix = Record<string, Record<string, boolean>>;
interface PermissionGroup {
  resource: string;
  order: number;
  label: string;
  description: string;
  permissions: PermissionDefinition[];
}

// Candidate + SUPER_ADMIN are sealed roles, not configurable here.
const EDITABLE_ROLES = ['ORG_ADMIN', 'AUTHOR', 'PROCTOR'] as const;
type EditableRole = (typeof EDITABLE_ROLES)[number];
const ROLE_LABELS: Record<string, string> = { ORG_ADMIN: 'Admin', AUTHOR: 'Author', PROCTOR: 'Proctor' };
const ROLE_ICONS: Record<string, LucideIcon> = { ORG_ADMIN: ShieldCheck, AUTHOR: FileText, PROCTOR: Eye };

type RolePermissionArrays = Record<string, string[]>;

function matrixFromRoleArrays(
  roleArrays: RolePermissionArrays | undefined,
  definitions: PermissionDefinition[],
): PermissionMatrix {
  const matrix: PermissionMatrix = {};
  for (const def of definitions) {
    matrix[def.key] = {};
    for (const role of EDITABLE_ROLES) {
      matrix[def.key][role] = roleArrays?.[role]?.includes(def.key) ?? false;
    }
  }
  return matrix;
}

function roleArraysFromMatrix(matrix: PermissionMatrix): RolePermissionArrays {
  const result: RolePermissionArrays = {};
  for (const role of EDITABLE_ROLES) {
    const keys: string[] = [];
    for (const [key, roles] of Object.entries(matrix)) {
      if (roles[role]) keys.push(key);
    }
    result[role] = keys;
  }
  return result;
}

const Permissions: React.FC = () => {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  const [isOwner, setIsOwner] = useState(false);
  const canEditRoles = usePermission(PERMISSIONS.ROLES_EDIT);
  const isAdmin = isOwner || canEditRoles;

  const [definitions, setDefinitions] = useState<PermissionDefinition[]>([]);
  const [serverPermissions, setServerPermissions] = useState<PermissionMatrix>({});
  const [localPermissions, setLocalPermissions] = useState<PermissionMatrix>({});
  const [defaults, setDefaults] = useState<PermissionMatrix>({});

  const [activeRole, setActiveRole] = useState<EditableRole>('ORG_ADMIN');
  const [selectedRole, setSelectedRole] = useState<EditableRole>('ORG_ADMIN');
  const [matrixOpen, setMatrixOpen] = useState(false);
  const isActiveRoleCustomized = useMemo(
    () =>
      definitions.some(
        (def) => (serverPermissions[def.key]?.[activeRole] ?? false) !== (defaults[def.key]?.[activeRole] ?? false),
      ),
    [definitions, serverPermissions, defaults, activeRole],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const fetchPermissions = useCallback(async () => {
    const orgId = authState.organization?.id;
    if (!orgId) return;
    setIsLoading(true);
    try {
      const { data } = (await Permission.Api.Get(orgId)) as any;
      const defs = data.definitions as PermissionDefinition[];
      const serverMatrix = matrixFromRoleArrays(data.rolePermissions, defs);
      setDefinitions(defs);
      setServerPermissions(serverMatrix);
      setLocalPermissions(serverMatrix);
      setDefaults(matrixFromRoleArrays(data.defaultRolePermissions, defs));
      setIsOwner(data.isOwner);
    } catch {
      toast.error(t('failed_to_load_permissions'));
    } finally {
      setIsLoading(false);
    }
  }, [authState.organization?.id, t]);
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasChanges = useMemo(
    () => JSON.stringify(localPermissions) !== JSON.stringify(serverPermissions),
    [localPermissions, serverPermissions],
  );

  const handleToggle = (key: string, value: boolean) => {
    if (!isAdmin) return;
    setLocalPermissions((prev) => ({ ...prev, [key]: { ...prev[key], [activeRole]: value } }));
  };
  const handleGroupToggleAll = (group: PermissionGroup, value: boolean) => {
    if (!isAdmin) return;
    setLocalPermissions((prev) => {
      const next = { ...prev };
      for (const perm of group.permissions) next[perm.key] = { ...next[perm.key], [activeRole]: value };
      return next;
    });
  };
  const handleSave = async () => {
    const orgId = authState.organization?.id;
    if (!orgId) return;
    setIsSaving(true);
    try {
      const { data } = (await Permission.Api.Update(orgId, roleArraysFromMatrix(localPermissions))) as any;
      const defs = (data.definitions as PermissionDefinition[]) ?? definitions;
      const serverMatrix = matrixFromRoleArrays(data.rolePermissions, defs);
      setServerPermissions(serverMatrix);
      setLocalPermissions(serverMatrix);
      toast.success(t('permissions_saved_successfully'));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save permissions';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };
  const handleReset = async () => {
    const orgId = authState.organization?.id;
    if (!orgId) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        t('role_permissions_will_be_reverted_to_system_defaults', {
          role: ROLE_LABELS[activeRole],
          defaultValue: "This will revert the {{role}} role's permissions to the system defaults. Continue?",
        }),
      )
    ) {
      return;
    }
    setIsResetting(true);
    try {
      const { data } = (await Permission.Api.ResetRole(orgId, activeRole)) as any;
      const defs = (data.definitions as PermissionDefinition[]) ?? definitions;
      const serverMatrix = matrixFromRoleArrays(data.rolePermissions, defs);
      setServerPermissions(serverMatrix);
      setLocalPermissions(serverMatrix);
      toast.success(t('permissions_reset_to_defaults'));
    } catch {
      toast.error(t('failed_to_reset_permissions'));
    } finally {
      setIsResetting(false);
    }
  };
  const handleDiscard = () => setLocalPermissions(serverPermissions);

  const groups = useMemo<PermissionGroup[]>(() => {
    const byResource = new Map<string, PermissionDefinition[]>();
    for (const def of definitions) {
      const resource = def.resource ?? def.category;
      const list = byResource.get(resource);
      if (list) list.push(def);
      else byResource.set(resource, [def]);
    }
    return Array.from(byResource.entries())
      .map(([resource, perms]) => ({
        resource,
        order: perms[0]?.resourceOrder ?? 999,
        label: t(`perm_resource_${resource}`, {
          defaultValue: t(`category_${perms[0]?.category}`, { defaultValue: resource }),
        }),
        description: t(`perm_resource_${resource}_desc`, { defaultValue: '' }),
        permissions: perms,
      }))
      .sort((a, b) => a.order - b.order);
  }, [definitions, t]);

  // Group-level summary for the cards + "what this role can do" checklist.
  const roleGroups = useCallback(
    (role: string) => {
      const allowed = groups.filter((g) => g.permissions.some((p) => serverPermissions[p.key]?.[role]));
      const denied = groups.filter((g) => g.permissions.every((p) => !serverPermissions[p.key]?.[role]));
      return { allowed, denied };
    },
    [groups, serverPermissions],
  );

  const openMatrix = (role: EditableRole) => {
    setActiveRole(role);
    setMatrixOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  const selected = roleGroups(selectedRole);

  return (
    <div className="w-full max-w-4xl space-y-6">
      {/* No page-level heading here — this page is only hosted inside the
          settings dialog, whose section title (SettingsDialog.tsx) is the
          single source of truth for the "Roles" heading. The subtitle still
          carries context the dialog doesn't show. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-muted-foreground">{t('roles_subtitle')}</p>
        {isAdmin && (
          <Button disabled title={t('coming_soon')} className="shrink-0">
            <Plus size={14} className="mr-2" />
            {t('new_role')}
          </Button>
        )}
      </div>

      {isOwner && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Shield size={16} className="shrink-0 text-muted-foreground" aria-hidden />
              <p className="text-sm text-muted-foreground">
                {t('as_the_organization_owner_you_have')}{' '}
                <span className="font-semibold text-foreground">{t('all_permissions_granted')}</span>{' '}
                {t('regardless_of_role_settings_below')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {!isAdmin && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Lock size={16} />
              <p className="text-sm">
                {t('you_are_viewing_permissions_in_read_only_mode_only_administrators_can_modify_permissions')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {EDITABLE_ROLES.map((role) => {
          const Icon = ROLE_ICONS[role];
          const areas = roleGroups(role).allowed;
          const isSel = selectedRole === role;
          return (
            <Card key={role} className={cn('overflow-hidden', isSel && 'border-foreground ring-1 ring-foreground')}>
              <button
                type="button"
                onClick={() => setSelectedRole(role)}
                className="block w-full p-4 text-left"
                aria-pressed={isSel}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground/70">
                  <Icon size={18} />
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground">{ROLE_LABELS[role]}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t(`role_desc_${role}`)}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {areas.slice(0, 3).map((g) => (
                    <span key={g.resource} className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {g.label}
                    </span>
                  ))}
                  {areas.length === 0 && (
                    <span className="text-[11px] text-muted-foreground">{t('perm_no_areas')}</span>
                  )}
                </div>
              </button>
              <div className="flex justify-end border-t border-border px-4 py-2">
                <button
                  type="button"
                  onClick={() => openMatrix(role)}
                  disabled={!isAdmin}
                  className="text-sm font-medium text-foreground underline-offset-[3px] hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  {t('edit')}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* What this role can do */}
      <Card>
        <CardHeader className="flex flex-col items-start gap-2 space-y-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <CardTitle className="text-base">
            {ROLE_LABELS[selectedRole]} — {t('what_this_role_can_do')}
          </CardTitle>
          <button
            type="button"
            onClick={() => openMatrix(selectedRole)}
            className="shrink-0 text-sm font-medium text-foreground underline-offset-[3px] hover:underline"
          >
            {t('open_full_matrix')}
          </button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {selected.allowed.map((g) => (
              <div key={g.resource} className="flex items-center gap-2 text-sm text-foreground">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
                  <Check size={12} />
                </span>
                {g.label}
              </div>
            ))}
            {selected.denied.map((g) => (
              <div key={g.resource} className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <X size={12} />
                </span>
                {g.label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Full permission matrix — the existing editor, in a dialog */}
      <Modal
        isOpen={matrixOpen}
        onClose={() => setMatrixOpen(false)}
        title={t('open_full_matrix')}
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              value={activeRole}
              defaultValue="ORG_ADMIN"
              onValueChange={(v) => setActiveRole(v as EditableRole)}
              className="min-w-0"
            >
              {/* TabsList is already inline-flex + max-w-full + overflow-x-auto:
                  a long role set scrolls inside the pill instead of wrapping out
                  of its fixed h-10 box. */}
              <TabsList>
                {EDITABLE_ROLES.map((role) => (
                  <TabsTrigger key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {isAdmin && isActiveRoleCustomized && (
              <Button variant="outline" size="sm" onClick={handleReset} disabled={isResetting} className="shrink-0 self-start sm:self-auto">
                {isResetting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RotateCcw size={14} className="mr-2" />}
                {t('reset_to_defaults')}
              </Button>
            )}
          </div>

          {isAdmin && isActiveRoleCustomized && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-muted-foreground">
              <Info size={16} className="shrink-0" />
              <p className="text-sm">
                {t('the_role_has_custom_permission_overrides', {
                  role: ROLE_LABELS[activeRole],
                  defaultValue: 'The {{role}} role has custom permission overrides.',
                })}
              </p>
            </div>
          )}

          <div className="max-h-[52vh] space-y-4 overflow-y-auto pr-1">
            {groups.map((group) => {
              const allChecked =
                group.permissions.length > 0 && group.permissions.every((perm) => localPermissions[perm.key]?.[activeRole]);
              return (
                <div key={group.resource} className="rounded-lg border border-border p-3 sm:p-4">
                  {/* Without wrapping, this row's min width is the label's
                      longest word + the shrink-0 select-all button, which
                      overflows a phone at long translations (uz "Hammasini
                      bekor qilish") and horizontally scrolls the whole dialog.
                      flex-wrap + a min-width floor on the label column drops
                      the button to its own line once the pair no longer fits,
                      so the label keeps enough room to wrap on word bounds
                      instead of being squeezed; break-words is the last-resort
                      guard for a single unbreakable word. */}
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                    <div className="min-w-[9rem] flex-1">
                      <p className="break-words text-[13px] font-semibold text-foreground">{group.label}</p>
                      {group.description && <p className="mt-1 break-words text-xs text-muted-foreground">{group.description}</p>}
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleGroupToggleAll(group, !allChecked)}
                        className="shrink-0 whitespace-nowrap text-sm font-medium text-foreground underline-offset-[3px] hover:underline"
                      >
                        {allChecked ? t('clear_all') : t('select_all')}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-3 sm:gap-x-6">
                    {group.permissions.map((perm) => {
                      const allowed = localPermissions[perm.key]?.[activeRole] ?? false;
                      const isChanged = (defaults[perm.key]?.[activeRole] ?? false) !== allowed;
                      const actionLabel = t(`perm_action_${perm.action}`, { defaultValue: perm.feature });
                      return (
                        <label
                          key={perm.key}
                          title={perm.description}
                          className={cn(
                            'flex min-w-0 select-none items-center gap-2',
                            isAdmin ? 'cursor-pointer' : 'cursor-default',
                          )}
                        >
                          <Checkbox
                            checked={allowed}
                            onCheckedChange={(value) => handleToggle(perm.key, value === true)}
                            disabled={!isAdmin}
                          />
                          <span className="break-words text-sm text-foreground">{actionLabel}</span>
                          {isChanged && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" title={t('changed_from_default')} />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {isAdmin && (
            <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end">
              {hasChanges && (
                <p className="mr-auto self-center text-sm text-muted-foreground">{t('you_have_unsaved_changes')}</p>
              )}
              <Button className="w-full sm:w-auto" variant="outline" onClick={handleDiscard} disabled={!hasChanges}>
                {t('discard')}
              </Button>
              <Button className="w-full sm:w-auto" onClick={handleSave} disabled={isSaving || !hasChanges}>
                {isSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                {t('save_changes_2')}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Permissions;
